import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { MotionConfig, MotionValue, animate, motion, useInView, useMotionTemplate, useMotionValue, useTransform } from "framer-motion";
import { guidelines, InfoTable } from "./thtData";
import { Reveal, SectionHeading, GroundEdge, Sprout, FloatingDecor, FloatingFluff, PageMeta } from "./ThtParts";
import "./ThtTop.css";
import "./ThtDesign.css";
import "./ThtBusiness.css";
import "./ThtCompany.css";
import "./ThtNatural.css";

const sections = [
  { id: "philosophy", label: "3つの指針" },
  { id: "recruit", label: "採用情報" },
  { id: "overview", label: "会社概要" },
];

// Mission=タネ / Vision=芽が育つ場所 / Value=花を咲かせる人 として、指針を植物の成長に重ねる
const guidelineMarks = ["seed", "sprout", "flower"];

// 3つの指針のセクション:カードの背後に季節の植物を描き、時間とともに季節が移り変わる(自動でくり返す)
// 春=チューリップ → 夏=ひまわり → 秋=コスモス → 冬=雪の積もった椿
const SEASONS = [0, 0.25, 0.5, 0.75, 1];
const SEASON_LOOP_SEC = 22; // 四季が1周する秒数
const seeded = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};



// 1つの季節の植物:その季節の間だけフェードインで現れ、季節が過ぎるとフェードアウトで消える
const SeasonPlant = ({
  p,
  center,
  wrap = false,
  children,
}: {
  p: MotionValue<number>;
  center: number;
  wrap?: boolean;
  children: React.ReactNode;
}) => {
  const range = wrap ? [0, 0.08, 0.16, 0.84, 0.92, 1] : [center - 0.16, center - 0.08, center + 0.08, center + 0.16];
  const opacity = useTransform(p, range, wrap ? [1, 1, 0, 0, 1, 1] : [0, 1, 1, 0]);
  return (
    <motion.g style={{ opacity }}>
      {children}
    </motion.g>
  );
};

const leafBlade = (x: number, y: number, h: number, lean: number) =>
  `M${x} ${y} C${x + lean * 0.4} ${y - h * 0.4} ${x + lean} ${y - h * 0.8} ${x + lean * 1.2} ${y - h} C${x + lean * 0.5} ${y - h * 0.7} ${x - 6} ${y - h * 0.4} ${x} ${y} Z`;

// 春:チューリップ(赤・ピンク・黄が並んで咲く)
const Tulips = () => {
  const rand = seeded(31);
  const colors = ["#e8504a", "#f48fb1", "#f6c23e", "#e8504a", "#f7a0c0", "#f6c23e", "#ef6f5e", "#f48fb1"];
  return (
    <g className="cmp-plant-sway">
      {colors.map((c, i) => {
        const x = 90 + i * 62 + rand() * 20;
        const h = 230 + rand() * 170;
        const top = 640 - h;
        const lean = (rand() - 0.5) * 30;
        return (
          <g key={i}>
            <path d={`M${x} 640 C${x + lean * 0.3} ${640 - h * 0.5} ${x + lean} ${top + 40} ${x + lean} ${top + 20}`} stroke="#5aa34c" strokeWidth="7" fill="none" strokeLinecap="round" />
            <path d={leafBlade(x - 4, 640, h * 0.55, -40 - rand() * 20)} fill="#6fb35d" />
            <path d={leafBlade(x + 4, 640, h * 0.45, 40 + rand() * 20)} fill="#5aa34c" />
            <g transform={`translate(${x + lean} ${top + 20})`}>
              <path d="M-26 -10 C-28 -46 -14 -60 0 -62 C14 -60 28 -46 26 -10 C20 6 -20 6 -26 -10 Z" fill={c} />
              <path d="M-10 -8 C-14 -40 -4 -56 6 -60 C10 -40 8 -16 -10 -8 Z" fill="#ffffff" opacity="0.25" />
              <path d="M0 -62 L-8 -46 L0 -52 L8 -46 Z" fill={c} />
            </g>
          </g>
        );
      })}
    </g>
  );
};

// 夏:ひまわり(背の高い大輪)
const Sunflower = ({ x, h, size }: { x: number; h: number; size: number }) => {
  const top = 640 - h;
  return (
    <g>
      <path d={`M${x} 640 C${x - 6} ${640 - h * 0.5} ${x + 8} ${top + 80} ${x} ${top}`} stroke="#4f8f3a" strokeWidth="12" fill="none" strokeLinecap="round" />
      <path d={`M${x} ${640 - h * 0.45} C${x - 70} ${640 - h * 0.5} ${x - 110} ${640 - h * 0.38} ${x - 120} ${640 - h * 0.3} C${x - 70} ${640 - h * 0.3} ${x - 30} ${640 - h * 0.36} ${x} ${640 - h * 0.45} Z`} fill="#5fa046" />
      <path d={`M${x} ${640 - h * 0.65} C${x + 70} ${640 - h * 0.7} ${x + 100} ${640 - h * 0.6} ${x + 110} ${640 - h * 0.52} C${x + 70} ${640 - h * 0.52} ${x + 30} ${640 - h * 0.56} ${x} ${640 - h * 0.65} Z`} fill="#4f8f3a" />
      <g transform={`translate(${x} ${top}) scale(${size})`}>
        {Array.from({ length: 22 }).map((_, i) => (
          <ellipse key={i} cx="0" cy="-46" rx="11" ry="30" fill={i % 2 ? "#f6c23e" : "#f8d04f"} transform={`rotate(${i * (360 / 22)})`} />
        ))}
        <circle r="34" fill="#6b3f1d" />
        <circle r="26" fill="#7d4a22" />
        {Array.from({ length: 18 }).map((_, i) => (
          <circle key={i} cx={Math.cos(i * 2.4) * (6 + (i % 6) * 3.4)} cy={Math.sin(i * 2.4) * (6 + (i % 6) * 3.4)} r="2" fill="#4a2a10" />
        ))}
      </g>
    </g>
  );
};
const Sunflowers = () => (
  <g className="cmp-plant-sway">
    <Sunflower x={170} h={430} size={0.9} />
    <Sunflower x={330} h={560} size={1.15} />
    <Sunflower x={470} h={380} size={0.8} />
  </g>
);

// 秋:コスモス(細い茎が風に揺れる)
const Cosmos = () => {
  const rand = seeded(57);
  const colors = ["#f7a0c0", "#ffffff", "#e0559a", "#f7a0c0", "#ffffff", "#e0559a", "#f7a0c0", "#f5b8d0", "#e0559a", "#ffffff"];
  return (
    <g>
      {colors.map((c, i) => {
        const x = 70 + i * 50 + rand() * 18;
        const h = 260 + rand() * 230;
        const lean = (rand() - 0.5) * 70;
        const top = { x: x + lean, y: 640 - h };
        return (
          <g key={i} className="cmp-plant-sway" style={{ animationDelay: `${-rand() * 4}s` }}>
            <path d={`M${x} 640 C${x} ${640 - h * 0.5} ${top.x} ${top.y + 60} ${top.x} ${top.y}`} stroke="#6aa04e" strokeWidth="3.5" fill="none" />
            {[0.35, 0.55].map((t) => (
              <path key={t} d={`M${x + lean * t} ${640 - h * t} l-22 -14 M${x + lean * t} ${640 - h * t} l20 -16 M${x + lean * t - 12} ${640 - h * t - 8} l-6 -14 M${x + lean * t + 11} ${640 - h * t - 9} l6 -14`} stroke="#6aa04e" strokeWidth="2" fill="none" strokeLinecap="round" />
            ))}
            <g transform={`translate(${top.x} ${top.y})`}>
              {Array.from({ length: 8 }).map((_, j) => (
                <path key={j} d="M0 0 C-9 -10 -8 -28 0 -32 C8 -28 9 -10 0 0 Z" fill={c} stroke={c === "#ffffff" ? "#f0d7e1" : "none"} transform={`rotate(${j * 45})`} />
              ))}
              <circle r="7" fill="#f6c23e" />
            </g>
          </g>
        );
      })}
    </g>
  );
};

// 冬:椿(濃い緑の葉に赤い花、上に雪が積もる)
const Camellia = () => {
  const rand = seeded(83);
  const leaves = Array.from({ length: 70 }).map(() => {
    const a = rand() * Math.PI;
    const d = Math.sqrt(rand());
    return { x: 300 + Math.cos(a) * 250 * d * (rand() > 0.5 ? 1 : -1), y: 640 - Math.sin(a) * 300 * d - 20, r: rand() * 360 };
  });
  const flowers = Array.from({ length: 12 }).map(() => {
    const a = rand() * Math.PI;
    const d = 0.3 + rand() * 0.65;
    return { x: 300 + Math.cos(a) * 230 * d, y: 640 - Math.sin(a) * 280 * d - 30 };
  });
  return (
    <g>
      <path d="M300 640 C296 600 290 560 300 520 M300 600 C270 580 250 560 236 530 M300 590 C330 570 350 550 362 520" stroke="#5e3c1f" strokeWidth="10" fill="none" strokeLinecap="round" />
      {leaves.map((l, i) => (
        <ellipse key={i} cx={l.x} cy={l.y} rx="24" ry="11" fill={i % 3 ? "#2f6b34" : "#3d7f40"} transform={`rotate(${l.r} ${l.x} ${l.y})`} />
      ))}
      {flowers.map((fl, i) => (
        <g key={i} transform={`translate(${fl.x} ${fl.y})`}>
          {Array.from({ length: 5 }).map((_, j) => (
            <circle key={j} cx={Math.cos((j * 72 * Math.PI) / 180) * 11} cy={Math.sin((j * 72 * Math.PI) / 180) * 11} r="12" fill={i % 4 === 0 ? "#f48fb1" : "#d62f3a"} />
          ))}
          <circle r="7" fill="#f6c23e" />
          {/* 花の上に積もる雪 */}
          <path d="M-18 -16 C-10 -26 10 -26 18 -16 C8 -19 -8 -19 -18 -16 Z" fill="#ffffff" />
        </g>
      ))}
      {/* 茂みの上に積もった雪 */}
      <path d="M70 520 C140 380 240 330 300 330 C360 330 460 380 530 520 C470 470 420 440 360 430 C330 445 270 445 240 430 C180 440 130 470 70 520 Z" fill="#ffffff" opacity="0.92" />
    </g>
  );
};

// 季節ごとに舞うもの(花びら・落ち葉・雪)
const Particles = ({ kind, opacity }: { kind: string; opacity: MotionValue<number> }) => (
  <motion.div className={`cmp-season-particles cmp-season-${kind}`} style={{ opacity }}>
    {Array.from({ length: 12 }).map((_, i) => (
      <span key={i} />
    ))}
  </motion.div>
);

const PhilosophySection = () => {
  const ref = useRef<HTMLElement>(null);
  // 季節は時間で自動的にくり返す(0=春 0.25=夏 0.5=秋 0.75=冬 1=春に戻る)
  const p = useMotionValue(0);
  const inView = useInView(ref, { amount: 0.15 });

  useEffect(() => {
    // 画面に入っている間だけ動かし、外れたら止める(止めた位置から再開)
    if (!inView) return;
    // 動きを減らす設定の人には、季節を切り替えず春のまま表示する
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const controls = animate(p, [0, 0, 0.25, 0.25, 0.5, 0.5, 0.75, 0.75, 1], {
      duration: SEASON_LOOP_SEC,
      times: [0, 0.18, 0.25, 0.43, 0.5, 0.68, 0.75, 0.93, 1], // 各季節で少し止まり、約1.5秒かけて次へ
      ease: "easeInOut",
      repeat: Infinity,
    });
    controls.time = p.get() * SEASON_LOOP_SEC;
    return () => controls.stop();
  }, [inView, p]);

  const skyTop = useTransform(p, SEASONS, ["#fde6ee", "#bfe0f5", "#f9d3a3", "#d9e3ee", "#fde6ee"]);
  const skyBottom = useTransform(p, SEASONS, ["#fff8f6", "#eef7fd", "#fff1dc", "#f7f9fc", "#fff8f6"]);
  const background = useMotionTemplate`linear-gradient(180deg, ${skyTop}, ${skyBottom})`;
  const groundBack = useTransform(p, SEASONS, ["#d4ecb8", "#9fd08c", "#e3c27a", "#ffffff", "#d4ecb8"]);
  const groundFront = useTransform(p, SEASONS, ["#bfe3a0", "#7fbf6c", "#d1a85c", "#eef3f8", "#bfe3a0"]);
  const sunOpacity = useTransform(p, [0.1, 0.25, 0.4], [0, 1, 0]);
  const petals = useTransform(p, [0, 0.12, 0.2, 0.9, 1], [1, 1, 0, 0, 1]);
  const leaves = useTransform(p, [0.38, 0.5, 0.6, 0.68], [0, 1, 1, 0]);
  const snow = useTransform(p, [0.65, 0.75, 0.85, 0.93], [0, 1, 1, 0]);

  return (
    <motion.section ref={ref} id="philosophy" className="biz-section cmp-philosophy" style={{ background }}>
      <div className="cmp-season" aria-hidden="true">
        <motion.span className="cmp-season-sun" style={{ opacity: sunOpacity }} />
        {/* 季節の植物:右側に大きく、季節が変わるとフェードイン・アウトで入れ替わる */}
        <svg className="cmp-season-tree cmp-season-plants" viewBox="0 0 600 640" preserveAspectRatio="xMidYMax meet">
          <SeasonPlant p={p} center={0} wrap><Tulips /></SeasonPlant>
          <SeasonPlant p={p} center={0.25}><Sunflowers /></SeasonPlant>
          <SeasonPlant p={p} center={0.5}><Cosmos /></SeasonPlant>
          <SeasonPlant p={p} center={0.75}><Camellia /></SeasonPlant>
        </svg>
        <Particles kind="petals" opacity={petals} />
        <Particles kind="leaves" opacity={leaves} />
        <Particles kind="snow" opacity={snow} />
        <svg className="cmp-season-ground" viewBox="0 0 1440 160" preserveAspectRatio="none">
          <motion.path d="M0 80 C240 40 480 60 720 84 C960 110 1200 50 1440 76 L1440 160 L0 160 Z" style={{ fill: groundBack }} />
          <motion.path d="M0 116 C300 88 520 106 760 120 C1020 136 1240 98 1440 112 L1440 160 L0 160 Z" style={{ fill: groundFront }} />
        </svg>
      </div>
      <div className="content-container">
        <Reveal className="biz-title" x={-48} y={0}>
          <span className="biz-title-en">Philosophy</span>
          <h2>THT合同会社が掲げる3つの指針</h2>
        </Reveal>
        <Reveal>
          <p className="cmp-intro">
            メンバー一人ひとりの行動や意思決定を導く指針として、「Mission」「Vision」「Value」を定めています。
          </p>
        </Reveal>
        <div className="cmp-guidelines">
          {guidelines.map((g, i) => (
            <Reveal key={g.label} delay={0.1} x={i % 2 === 0 ? -48 : 48} y={0}>
              <article className={`cmp-guideline cmp-mark-${guidelineMarks[i]}`}>
                <div className="cmp-guideline-head">
                  <span className="cmp-guideline-icon" aria-hidden="true" />
                  <div>
                    <span className="cmp-guideline-label">{g.label}</span>
                    <span className="cmp-guideline-sub">{g.sub}</span>
                  </div>
                </div>
                <h3>{g.title}</h3>
                <p>{g.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

const recruitRows = [
  { label: "雇用形態", value: "正社員" },
  { label: "勤務時間", value: "9:00〜18:00(実働8時間)" },
  { label: "休日", value: "年間休日120日・週休2日制\n1月/5月/8月/12月は1週間の休暇あり\n※休暇の曜日・日程は担当店舗によって異なります" },
  { label: "試用期間", value: "あり(3ヵ月間)" },
  { label: "福利厚生", value: "雇用保険・労災保険・健康保険・厚生年金" },
];

// 社員交流イベントの写真
const events = [
  { label: "海外旅行", srcs: ["/images/event-travel.jpg"] },
  { label: "食事会・BBQ", srcs: ["/images/event-bbq.jpg"] },
  { label: "レクリエーション", srcs: ["/images/event-recreation-snow.jpg", "/images/event-recreation-games.jpg"] },
];

const recruitFlow = ["カジュアル面談", "書類選考", "最終面接", "内定", "入社"];

const companyRows = [
  { label: "会社名", value: "THT合同会社" },
  { label: "代表者", value: "代表 高田知己" },
  { label: "設立", value: "2024年8月" },
  { label: "資本金", value: "30万円" },
  { label: "所在地", value: "〒920-1155\n石川県金沢市田上本町4丁目40番地" },
  { label: "事業内容", value: "スポーツクラブの運営\nMTT大会・進級テストの開催\n学習塾の運営" },
  { label: "役員", value: "代表 高田知己\n役員 高田隆徳\n役員 高田治吉" },
];

const scrollTo = (e: React.MouseEvent, id: string) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ブラウザのタブに表示するページ名と、検索結果に出る説明
const pageMeta = {
  title: "会社案内｜THT合同会社",
  description: "THT合同会社の3つの指針(Mission・Vision・Value)、採用情報、会社概要をご紹介します。",
  canonical: "https://tht-group.co.jp/company",
};

const ThtCompany = () => {
  const { hash } = useLocation();
  // 旧採用情報(/recruit)などから #recruit 付きで来たときは、最初からその位置を表示する
  useEffect(() => {
    if (!hash) return;
    const timer = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" }), 0);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
  <>
    <PageMeta {...pageMeta} />
    <MotionConfig reducedMotion="user">
    <div className="tht-home biz cmp">
      {/* ページの見出し */}
      <section className="biz-hero">
        <FloatingDecor />
        <div className="content-container">
          <SectionHeading en="Company" ja="会社案内" />
          <Reveal delay={0.1}>
            <p className="lead-text">
              THT合同会社が大切にしている考え方と、一緒に働く仲間の募集、会社の基本情報をご紹介します。
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <nav className="biz-anchors">
              {sections.map((s) => (
                <a key={s.id} href={`#${s.id}`} onClick={(e) => scrollTo(e, s.id)}>
                  <Sprout className="cls-anchor-sprout" />
                  {s.label}
                </a>
              ))}
            </nav>
          </Reveal>
        </div>
      </section>

      {/* 3つの指針:背景全体が空になり、スクロールで朝→昼→夕方へ変わる */}
      <PhilosophySection />
      {/* 採用情報 */}
      <section id="recruit" className="biz-section biz-section-tint cmp-recruit">
        <GroundEdge color="#fdf3ee" />
        <div className="biz-section-body cmp-recruit-body">
          {/* 新しい場所へ旅立つ綿毛:新しい仲間を迎えるイメージ */}
          <FloatingFluff count={10} />
          <FloatingDecor />
          <div className="content-container">
            <Reveal className="biz-title" x={48} y={0}>
              <span className="biz-title-en">Recruit</span>
              <h2>採用情報</h2>
            </Reveal>
            <Reveal className="cmp-recruit-message">
              <h3 className="biz-catch">「できた」を、一緒につくる人へ。</h3>
              <p>
                子どもの成長を、心から楽しめること。それが何よりの資格です。
                専門的なスキルや経験がなくても構いません。
              </p>
            </Reveal>

            <div className="cmp-recruit-grid">
              <Reveal className="cmp-card">
                <h4>募集要項</h4>
                <InfoTable rows={recruitRows} />
              </Reveal>
              <div className="cmp-recruit-side">
                <Reveal className="cmp-card" delay={0.1}>
                  <h4>人事評価</h4>
                  {/* 2つの評価(並列)をもとに給与・賞与を決め、納得度アンケートで見直す */}
                  <div className="cmp-eval">
                    <div className="cmp-eval-pair">
                      <div>
                        <strong>役員評価</strong>
                        <span>各役員が求める人材像を満たしているか</span>
                      </div>
                      <div>
                        <strong>成果評価</strong>
                        <span>ミッション達成のために自分で立てた目標の達成度</span>
                      </div>
                    </div>
                    <div className="cmp-eval-arrow" aria-hidden="true" />
                    <div className="cmp-eval-step">
                      <strong>評価の反映</strong>
                      <span>2つの評価をもとに給与・賞与を決定</span>
                    </div>
                    <div className="cmp-eval-arrow" aria-hidden="true" />
                    <div className="cmp-eval-step">
                      <strong>納得度アンケート</strong>
                      <span>評価された側が納得できる仕組みにするため、評価についてアンケートを実施</span>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>

            {/* 社員交流:写真で雰囲気を伝える(4つは並列なので番号なし) */}
            <Reveal className="cmp-card cmp-events-card">
              <h4>社員交流</h4>
              <p className="cmp-small">
                社員同士の交流を大切にしており、海外旅行や食事会、レクリエーションなどのイベントを実施しています。
              </p>
              <ul className="cmp-events">
                {events.map((ev) => (
                  // 同じイベントの写真はまとめて、名前を真ん中に1つだけ表示する
                  <li
                    key={ev.label}
                    className="cmp-event"
                    style={{ gridColumn: `span ${ev.srcs.length}` }}
                  >
                    <div className="cmp-event-imgs" style={{ gridTemplateColumns: `repeat(${ev.srcs.length}, 1fr)` }}>
                      {ev.srcs.map((src) => (
                        <img key={src} src={src} alt={ev.label} loading="lazy" />
                      ))}
                    </div>
                    <span>{ev.label}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            {/* 採用フロー(実際に順番がある流れ) */}
            <Reveal className="cmp-card cmp-flow-card">
              <h4>採用フロー</h4>
              <ol className="cmp-flow">
                {recruitFlow.map((step, i) => (
                  <li key={step}>
                    <span className="cmp-flow-num">STEP {i + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
            </Reveal>
            <Reveal className="btn-row">
              <Link to="/contact" className="tht-btn btn-orange">
                応募・カジュアル面談のお問い合わせ <span>&rarr;</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 会社概要 */}
      <section id="overview" className="biz-section cmp-overview">
        <FloatingDecor />
        <div className="content-container">
          <Reveal className="biz-title" x={-48} y={0}>
            <span className="biz-title-en">Overview</span>
            <h2>会社概要</h2>
          </Reveal>
          <Reveal className="cmp-card cmp-overview-card">
            <InfoTable rows={companyRows} />
          </Reveal>
        </div>
      </section>
    </div>
  </MotionConfig>
    </>
  );
};

export default ThtCompany;
